import { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import styles from './index.module.scss'
import { forwardRef } from "react"
import LabelBox from "../LabelBox"
import ImageRender from "../ImageRender"
import LabelToolBox from "../LabelToolBox"
import VideoController from "../VideoController"
import { message } from "antd";
import Utils from '@/utils/utils';
import { VideoContextProvider } from "../../hooks/VideoContext";

const ImageRenderBox = forwardRef((props, ref) => {
    const dropRef = useRef()
    const { item, namePlacement } = props
    const renderKey = item?.id || item?.imageId || item?.url || item?.name

    const imageRenderRef = useRef()
    useImperativeHandle(ref, () => ({
        getTransform: () => imageRenderRef.current?.getTransform?.(),
        setTransform: (nextTransform, meta = {}) => imageRenderRef.current?.setTransform?.(nextTransform, meta),
        resetTransform: (meta = {}) => imageRenderRef.current?.resetTransform?.(meta),
        isTransformed: () => imageRenderRef.current?.isTransformed?.() || false,
        getVideoState: () => imageRenderRef.current?.getVideoState?.() || null,
        syncVideo: (event) => imageRenderRef.current?.syncVideo?.(event),
    }), [])

    const isVideo = useMemo(() => {
        return isVideoUrl(item?.url)
    }, [item])

    const annotationsRef = useRef(item?.annotations || [])
    useEffect(() => {
        annotationsRef.current = item?.annotations || []
    }, [item?.annotations])

    const commentAnnotation = useMemo(() => getCommentAnnotation(item?.annotations || []), [item?.annotations])
    const frameAnnotations = useMemo(() => getFrameAnnotations(item?.annotations || []), [item?.annotations])
    const canvasAnnotations = useMemo(() => getCanvasAnnotations(item?.annotations || []), [item?.annotations])
    const shouldShowLabelTool = useMemo(() => {
        if (!item) return false
        if (item.readonly) {
            return !!props.allowReadonlyLabelTool && (!!commentAnnotation || frameAnnotations.length > 0)
        }
        return true
    }, [commentAnnotation, frameAnnotations, item, props.allowReadonlyLabelTool])
    const useFloatingLabelTool = useMemo(() => {
        return props.labelToolDisplay === 'floating'
    }, [props.labelToolDisplay])

    const handleLabelChange = (labels) => {
        const latestAnnotations = annotationsRef.current || []
        const mergedAnnotations = mergeAnnotations({
            annotations: latestAnnotations,
            canvasAnnotations: labels,
        })
        annotationsRef.current = mergedAnnotations
        props.onLabel(mergedAnnotations, item.imageId)
    }

    const handleCommentSave = (commentText) => {
        const mergedAnnotations = mergeAnnotations({
            annotations: annotationsRef.current || [],
            commentText,
        })
        annotationsRef.current = mergedAnnotations
        props.onLabel(mergedAnnotations, item.imageId)
    }

    const handleFrameLabelsChange = (labels) => {
        const mergedAnnotations = mergeAnnotations({
            annotations: annotationsRef.current || [],
            frameAnnotations: labels,
        })
        annotationsRef.current = mergedAnnotations
        props.onLabel(mergedAnnotations, item.imageId)
    }

    return (
        <div
            className={styles.ImageRenderBox}
            ref={dropRef}
        >
            <div className={styles.ImageRenderWrap}>
            {
                item ?
                <ImageRenderContainer
                    ref={imageRenderRef}
                    key={renderKey}
                    src={item.url}
                    initialTransform={props.initialTransform}
                    overlaySrc={item.overlayUrl}
                    name={item.name}
                    namePlacement={namePlacement}
                    extraName={item.extraName}
                    canvasLabels={!isVideo ? canvasAnnotations : []}
                    readonly={item.readonly}
                    onLabel={handleLabelChange}
                    onTransform={props.onTransform}
                    // 视频播放事件必须继续下传，才能由父级同步锁转发到关联画布。
                    onVideoEvent={props.onVideoEvent}
                >
                {
                    item?.extraRender?.()
                }
                </ImageRenderContainer> : null
            }
            </div>
            {
                shouldShowLabelTool ?
                <div
                    className={`${styles.FloatingLabelTool} ${useFloatingLabelTool && props.labelToolPlacement === 'right' ? styles.Right : styles.Centered} ${props.labelToolVisible === false ? styles.Hidden : ''}`}
                    style={isVideo ? {bottom: 44} : null}
                >
                <LabelToolBox
                    onChange={value => imageRenderRef.current.changeMode(value)}
                    onUndo={() => imageRenderRef.current.undo()}
                    onReset={() => imageRenderRef.current.reset()}
                    onImageUpload={(url) => imageRenderRef.current.addImage(url)}
                    commentValue={commentAnnotation?.comment || ''}
                    onCommentSave={handleCommentSave}
                    canvasAnnotations={canvasAnnotations}
                    isVideo={isVideo}
                    src={item.url}
                    readonly={item.readonly}
                    frameLabels={frameAnnotations}
                    onFrameLabelsChange={handleFrameLabelsChange}
                    canvasToolsDisabled={props.canvasToolsDisabled}
                    onConfirm={props.onConfirm}
                    confirmText={props.confirmText}
                />
                </div> : null
            } {
                (item && isVideo) ?
                <div className={styles.VideoControlWrap}>
                    <VideoController />
                </div> : null
            }
        </div>
    )
})

const ImageRenderBoxWithVideoContext = forwardRef((props, ref) => (
    <VideoContextProvider onTogglePlayPause={props.onVideoToggle}>
        <ImageRenderBox {...props} ref={ref} />
    </VideoContextProvider>
))

export default ImageRenderBoxWithVideoContext

// 帧评论，结构为 { label: string, snapshot: url, time: "2.39"  }
var isFrameCommentAnnotation = (item) => {
    if (!item || typeof item !== 'object') return false
    return !!item.snapshot
}

// 修改意见, 兼容结构为 { label: string, timestamp: datetime } 或 { text: string, timestamp: datetime } 或 { comment: string, timestamp: datetime }
var isCommentAnnotation = (item) => {
    if (!item || typeof item !== 'object') return false
    return (!!item.label || !!item.text || !!item.comment) && !item.snapshot
}

// 兼容历史数据
var getCommentAnnotation = (annotations = []) => {
    const item = annotations.find(isCommentAnnotation) || null
    if (!item) return null
    return {
        comment: item.comment || item.text || item.label,
        timestamp: item.timestamp,
    }
}

var getFrameAnnotations = (annotations = []) => {
    return annotations.filter(isFrameCommentAnnotation)
}

var getCanvasAnnotations = (annotations = []) => {
    return annotations.filter(item => !isCommentAnnotation(item) && !isFrameCommentAnnotation(item))
}

var mergeAnnotations = ({
    annotations = [],
    canvasAnnotations = getCanvasAnnotations(annotations),
    frameAnnotations = getFrameAnnotations(annotations),
    commentText = getCommentAnnotation(annotations)?.comment || '',
}) => {
    const merged = [
        ...canvasAnnotations,
        ...frameAnnotations,
    ]
    if (!commentText) return merged
    return [
        ...merged,
        { comment: commentText, timestamp: Date.now() },
    ]
}

var ImageRenderContainer = forwardRef((props, ref) => {
    const [transform, setTransform] = useState(props.initialTransform)
    const [imgSize, setImgSize] = useState({ width: 0, height: 0 })

    const handleLoad = (e) => {
        setImgSize({
            width: e.clientWidth,
            height: e.clientHeight
        })
    }

    const style = useMemo(() => {
        if (!transform) return
        return {
            display: imgSize.width == 0 || imgSize.height == 0 ? "none" : "block",
            width: imgSize.width,
            height: imgSize.height,
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0px) scale3d(${transform.scale}, ${transform.scale}, 1) rotate(0deg)`
        }
    }, [transform, imgSize])

    const overlayStyle = useMemo(() => {
        if (!transform) return
        return {
            display: imgSize.width == 0 || imgSize.height == 0 ? "none" : "block",
            width: imgSize.width,
            height: imgSize.height,
            left: '50%',
            top: '50%',
            transform: `translate(-50%, -50%) translate3d(${transform.x}px, ${transform.y}px, 0px) scale3d(${transform.scale}, ${transform.scale}, 1) rotate(0deg)`,
        }
    }, [transform, imgSize])

    const imageRenderRef = useRef()
    const onWheel = e => imageRenderRef.current.onWheel(e)
    const onMouseMove = e => imageRenderRef.current.onMouseMove(e)
    const onMouseLeave = e => imageRenderRef.current.onMouseUp(e)

    // 标注工具
    // rect/ellipse/brush/arrow/text
    const [mode, setMode] = useState()

    const imageReady = useMemo(() => {
        return imgSize.width * imgSize.height > 0
    }, [imgSize])

    const handleCopy = (text) => {
        Utils.copyText(text)
        message.success(`"${text}" 已复制`)
    }

    const labelBoxRef = useRef()
    const handleUndo = () => labelBoxRef.current && labelBoxRef.current.handleUndo()
    const handleReset = () => labelBoxRef.current && labelBoxRef.current.handleReset()
    const handleAddImage = async (url) => {
        if (!labelBoxRef.current?.handleAddImage) return
        await labelBoxRef.current.handleAddImage(url)
    }

    useImperativeHandle(ref, () => ({
        changeMode: setMode,
        undo: handleUndo,
        reset: handleReset,
        addImage: handleAddImage,
        getTransform: () => imageRenderRef.current?.getTransform?.(),
        setTransform: (nextTransform, meta = {}) => imageRenderRef.current?.setTransform?.(nextTransform, meta),
        resetTransform: (meta = {}) => imageRenderRef.current?.resetTransform?.(meta),
        isTransformed: () => imageRenderRef.current?.isTransformed?.() || false,
        // 外层同步锁通过 ImageRenderBox 的 ref 控制实际 video，需穿透此标注容器。
        getVideoState: () => imageRenderRef.current?.getVideoState?.() || null,
        syncVideo: (event) => imageRenderRef.current?.syncVideo?.(event),
    }))

    return (
        <div
            className={styles.ImageRenderContainer}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
        >
            <ImageRender
                ref={imageRenderRef}
                src={props.src}
                initialTransform={props.initialTransform}
                onTransform={(nextTransform, meta = {}) => {
                    setTransform(nextTransform)
                    props.onTransform?.(nextTransform, meta)
                }}
                onVideoEvent={props.onVideoEvent}
                onLoad={handleLoad}
            />
            {props.overlaySrc ? (
                <img
                    src={props.overlaySrc}
                    alt=""
                    aria-hidden="true"
                    className={styles.ImageOverlay}
                    style={overlayStyle}
                    draggable={false}
                />
            ) : null}
            {imageReady ?
            <>
            <LabelBox
                ref={labelBoxRef}
                style={style}
                transform={transform}
                width={imgSize.width}
                height={imgSize.height}
                labels={props.canvasLabels}
                readonly={props.readonly}
                mode={mode}
                onWheel={onWheel}
                onLabelChange={props.onLabel}
            />
            {
                props.name && props.namePlacement ?
                <div className={styles.ImageName} style={props.namePlacement == "left" ? { left: 8 } : { right: 8 }}>
                    {props.name}
                </div> : null
            } {
                props.extraName &&
                <div className={styles.ImageExtraName} onClick={() => handleCopy(props.extraName)}>
                    {props.extraName}
                </div>
            }
            {props.children}
            </> : null}
        </div>
    )
})

// 常见视频扩展名，用于判断 url 是否为视频资源
const VIDEO_EXTENSIONS = /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i

/** 根据 url 扩展名判断是否为视频 */
function isVideoUrl(url) {
    if (!url) return false
    return VIDEO_EXTENSIONS.test(url)
}
